import { observer } from 'mobx-react-lite'
import UserManagementStore from './UserManagementStore'
import useStore from 'reusable/mobx-store-utils'
import ButtonWithProgress from 'reusable/mui/progress-button/ButtonWithProgress'
import GapDataGrid from 'reusable/mui/data-grid/GapDataGrid'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import TextFieldEmail from 'reusable/mui/text-field-email/TextFieldEmail'
import Box from '@mui/material/Box'
import GapDialog from 'reusable/gap-dialog/GapDialog'
import DuplicateUserSearch from './DuplicateUserSearch'
import { useState } from 'react'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import SettingsAccessibilityIcon from '@mui/icons-material/SettingsAccessibility'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'

export default observer(function DevSettings() {
   const [value, setValue] = useState(0)

   const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
      setValue(newValue)
   }

   return <>
      <Tabs value={value} onChange={handleChange} sx={{ mb: 6 }}>
         <Tab icon={<ManageAccountsIcon />} label="User management" />
         <Tab icon={<SettingsAccessibilityIcon />} label="Duplicate user search" />
      </Tabs>
      {value === 0 && <UserManagement />}
      {value === 1 && <DuplicateUserSearch />}
   </>
})

const UserManagement = observer(function UserManagement() {
   const userManagementStore = useStore(() => new UserManagementStore())

   return <>
      <Box sx={{ display: 'flex', gap: 1, mt: 4, mb: 4 }}>
         {userManagementStore.formHandler.email.bindInput(ip =>
            <TextFieldEmail
               autoFocus
               fullWidth
               label="Email"
               ip={ip}
            />)}

         <Box sx={{ flex: 1 }}>
            <ButtonWithProgress
               loading={userManagementStore.userLoader.isLoading}
               variant="outlined"
               fullWidth
               sx={{ height: '56px', borderRadius: '8px', flex: 1, width: '220px' }}
               onClick={userManagementStore.search}>
               Search
            </ButtonWithProgress>
         </Box>
      </Box>

      {!!userManagementStore.users?.length && <>
         <Typography variant="body1">Click on a user to more information</Typography>

         {!userManagementStore.issues.length && <Typography variant="body1" sx={{ color: 'success.main', mt: 2 }}>
            ✓ No issues found with this user(s) or their attached orgs
         </Typography>
         }

         {!!userManagementStore.issues.length && <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1, color: 'error.main' }}>
               Issues Found ({userManagementStore.issues.length})
            </Typography>
            <Stack gap={1}>
               {userManagementStore.issues.map((issue, index) => (
                  <Box
                     key={`${issue.id}-${index}`}
                     sx={{
                        p: 2,
                        backgroundColor: 'error.lighter',
                        borderLeft: 4,
                        borderColor: 'error.main',
                        borderRadius: 1,
                     }}
                  >
                     <Typography variant="body2" sx={{ color: 'error.dark' }}>
                        {issue.issue}
                     </Typography>
                  </Box>
               ))}
            </Stack>
         </Box>}
      </>}

      <GapDataGrid
         columns={userManagementStore.userTableColumns}
         rows={userManagementStore.users || []}
         loading={userManagementStore.userLoader.isLoading}
         noRowsText='No users found'
         onCellClick={userManagementStore.onUserCellClick}
         hideToolbar
      />

      <GapDialog
         modalState={userManagementStore.userOrgModalState}
         title="User information"
         size="large"
         cancel
      >
         {userManagementStore.selectedUser && <Stack gap={2} sx={{ minHeight: '500px' }}>
            <Typography variant="body1">
               User: {userManagementStore.selectedUser.firstName} {userManagementStore.selectedUser.lastName} ({userManagementStore.selectedUser.email})
            </Typography>
            <Typography variant="body1">Id: {userManagementStore.selectedUser.id}</Typography>
            <Typography variant="body1">Invite accepted on: {userManagementStore.selectedUser.inviteAcceptedOn ? new Date(userManagementStore.selectedUser.inviteAcceptedOn).toLocaleDateString() : 'Not set'}</Typography>

            <GapDataGrid
               columns={userManagementStore.selectedUserTableColumns}
               rows={userManagementStore.selectedUser.orgs}
               loading={userManagementStore.userLoader.isLoading}
               noRowsText='No orgs found for user'
               hideToolbar
            />
         </Stack>}
      </GapDialog>
   </>
})
